/**
 * ClientCard — one ranked client row on the Portfolio page (SYR-85).
 *
 * Rank · client identity + bucket + signals · sparkline · BE score block ·
 * actions. Clicking the card opens the in-portal scorecard; the token-swap
 * "Open dashboard" stays as a secondary action.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import {
    ExportOutlined,
    MoreOutlined,
    RightOutlined,
} from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { THEME } from '../../components/MarketerView/config/theme';
import {
    bandFromClassification,
    formatBipolarScore,
} from '../../components/MarketerView/config/bandColours';
import { colourForScore } from '../../components/MarketerView/config/elementColours';
import { BandTag } from '../../components/MarketerView/atoms';
import type {
    IAgencyAnalyticsBrand,
    IAgencyAnalyticsClient,
} from '../../services/nestjs-base-api';
import {
    BucketBadges,
    DeltaChip,
    SignalChip,
    benchmarkPositionLabel,
    secondaryBtnStyle,
} from './agencyUi';
import { SparkTrend } from './SparkTrend';

interface ClientCardProps {
    client: IAgencyAnalyticsClient;
    brand: IAgencyAnalyticsBrand;
    rank: number | null;
    period: string;
    onOpenDashboard: (clientOrgId: number) => void;
    onRemove: (client: IAgencyAnalyticsClient) => void;
    openDashboardDisabled?: boolean;
}

export const ClientCard: React.FunctionComponent<ClientCardProps> = ({
    client,
    brand,
    rank,
    period,
    onOpenDashboard,
    onRemove,
    openDashboardDisabled,
}) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const { equity, benchmark } = brand;
    const scoreColour = colourForScore('Brand Equity', equity.score);
    const positionLabel = benchmarkPositionLabel(benchmark?.quartile);

    const menu: MenuProps = {
        items: [
            {
                key: 'remove',
                danger: true,
                label: intl.formatMessage({
                    id: 'agency.portfolio.removeClient',
                    defaultMessage: 'Remove client',
                }),
            },
        ],
        onClick: ({ key, domEvent }) => {
            domEvent.stopPropagation();
            if (key === 'remove') onRemove(client);
        },
    };

    const goScorecard = (): void =>
        navigate(`/agency/clients/${client.clientOrganisationId}`);

    return (
        <div
            className="be-card lift"
            onClick={goScorecard}
            style={{
                background: THEME.bgCard,
                border: `1px solid ${THEME.border}`,
                borderRadius: 16,
                boxShadow: THEME.cardShadow,
                padding: '20px 24px',
                cursor: 'pointer',
                display: 'grid',
                gridTemplateColumns: 'minmax(240px, 1.3fr) minmax(170px, 1fr) 230px auto',
                gap: 24,
                alignItems: 'center',
            }}
        >
            {/* Identity */}
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', minWidth: 0 }}>
                <span
                    style={{
                        fontSize: 15,
                        fontWeight: 800,
                        color: rank ? THEME.textMuted : 'transparent',
                        fontVariantNumeric: 'tabular-nums',
                        lineHeight: '28px',
                        minWidth: 26,
                    }}
                >
                    {rank ? `#${rank}` : '—'}
                </span>
                <div style={{ minWidth: 0 }}>
                    <div
                        style={{
                            fontSize: 18,
                            fontWeight: 700,
                            letterSpacing: '-0.01em',
                            color: THEME.textBase,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {client.clientOrganisationName}
                        {brand.brandName !== client.clientOrganisationName && (
                            <span style={{ color: THEME.textSecondary, fontWeight: 500 }}>
                                {' · '}
                                {brand.brandName}
                            </span>
                        )}
                    </div>
                    <div style={{ marginTop: 6 }}>
                        <BucketBadges bucket={brand.bucket} />
                    </div>
                    {/* insufficient_data is omitted — the score block already
                        shows the "Collecting data · n of 10" state. */}
                    {brand.signals.some((s) => s.type !== 'insufficient_data') && (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                            {brand.signals
                                .filter((s) => s.type !== 'insufficient_data')
                                .map((s) => (
                                    <SignalChip key={s.type} signal={s} />
                                ))}
                        </div>
                    )}
                    {client.notes && (
                        <div
                            style={{
                                marginTop: 8,
                                fontSize: 12.5,
                                color: THEME.textMuted,
                                fontStyle: 'italic',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 360,
                            }}
                            title={client.notes}
                        >
                            {client.notes}
                        </div>
                    )}
                </div>
            </div>

            {/* Sparkline */}
            <div onClick={(e) => e.stopPropagation()} style={{ minWidth: 0 }}>
                <SparkTrend brandId={brand.brandId} period={period} />
            </div>

            {/* Score block */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {equity.insufficientSampleSize ? (
                    <>
                        <span style={{ fontSize: 22, fontWeight: 800, color: THEME.textMuted }}>
                            <FormattedMessage
                                id="agency.portfolio.collectingData"
                                defaultMessage="Collecting data"
                            />
                        </span>
                        <span style={{ fontSize: 12.5, color: THEME.textSecondary }}>
                            <FormattedMessage
                                id="agency.portfolio.collectingDetail"
                                defaultMessage="{n} of 10 responses needed"
                                values={{ n: equity.totalResponses }}
                            />
                        </span>
                    </>
                ) : (
                    <>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                            <span
                                style={{
                                    fontSize: 34,
                                    fontWeight: 800,
                                    letterSpacing: '-0.02em',
                                    lineHeight: 1,
                                    color: scoreColour,
                                    fontVariantNumeric: 'tabular-nums',
                                }}
                            >
                                {formatBipolarScore(equity.score)}
                            </span>
                            <DeltaChip delta={equity.delta} />
                        </div>
                        <div>
                            <BandTag
                                classification={equity.classificationLabel ?? null}
                                band={bandFromClassification(equity.classificationLabel ?? null)}
                            />
                        </div>
                        <span style={{ fontSize: 12.5, color: THEME.textSecondary }}>
                            <FormattedMessage
                                id="agency.portfolio.responsesMeta"
                                defaultMessage="{n} responses"
                                values={{ n: equity.totalResponses }}
                            />
                            {positionLabel && benchmark?.available && (
                                <>
                                    {' · '}
                                    <FormattedMessage
                                        id={positionLabel.id}
                                        defaultMessage={positionLabel.def}
                                    />
                                </>
                            )}
                        </span>
                    </>
                )}
            </div>

            {/* Actions */}
            <div
                onClick={(e) => e.stopPropagation()}
                style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
                <button type="button" style={secondaryBtnStyle} onClick={goScorecard}>
                    <FormattedMessage id="agency.portfolio.scorecard" defaultMessage="Scorecard" />
                    <RightOutlined style={{ fontSize: 11 }} />
                </button>
                <Tooltip
                    title={
                        openDashboardDisabled
                            ? intl.formatMessage({
                                  id: 'agency.portfolio.openDisabled',
                                  defaultMessage: 'Unavailable while impersonating',
                              })
                            : intl.formatMessage({
                                  id: 'agency.portfolio.openDashboard',
                                  defaultMessage: "Open the client's full dashboard",
                              })
                    }
                >
                    <button
                        type="button"
                        disabled={openDashboardDisabled}
                        onClick={() => onOpenDashboard(client.clientOrganisationId)}
                        style={{
                            ...secondaryBtnStyle,
                            padding: '9px 12px',
                            opacity: openDashboardDisabled ? 0.5 : 1,
                            cursor: openDashboardDisabled ? 'not-allowed' : 'pointer',
                        }}
                    >
                        <ExportOutlined />
                    </button>
                </Tooltip>
                <Dropdown menu={menu} trigger={['click']}>
                    <button
                        type="button"
                        style={{ ...secondaryBtnStyle, padding: '9px 12px' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <MoreOutlined />
                    </button>
                </Dropdown>
            </div>
        </div>
    );
};
